/* "Continue shopping" floating chip for the homepage.
 *
 * Reads the most-recent entry from /services localStorage (last product
 * the visitor opened) and renders a slim pill in the lower-right of the
 * viewport. Clicking it deep-links back to that product's PDP.
 *
 * - Hidden entirely for first-time visitors (no entries in localStorage).
 * - Sessionally dismissible via the X button (sessionStorage flag).
 * - Mounted only on Home, so it auto-unmounts on every other route.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingBag, X, ArrowRight } from 'lucide-react';
import { getRecentlyViewed } from '../lib/recentlyViewed';

const DISMISS_KEY = 'zhelta:home:continue-chip-dismissed';

export default function ContinueShoppingChip() {
  const [entry, setEntry] = useState(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      if (window.sessionStorage.getItem(DISMISS_KEY) === '1') return;
    } catch { /* ignore */ }
    const recent = getRecentlyViewed();
    if (recent.length > 0) setEntry(recent[0]);
  }, []);

  if (!entry) return null;

  const handleDismiss = (e) => {
    e.preventDefault();
    e.stopPropagation();
    try { window.sessionStorage.setItem(DISMISS_KEY, '1'); } catch { /* ignore */ }
    setEntry(null);
  };

  return (
    <Link
      to={`/services/p/${encodeURIComponent(entry.slug)}`}
      data-testid="home-continue-shopping-chip"
      className="group fixed z-40 bottom-5 left-5 md:bottom-6 md:left-6 inline-flex items-center gap-3 pl-2 pr-4 py-2 rounded-full bg-white border border-[#E5E5E7] shadow-[0_18px_40px_-18px_rgba(14,14,16,0.35)] hover:border-[#c9a227]/60 hover:shadow-[0_24px_50px_-22px_rgba(201,162,39,0.55)] transition-all"
      style={{ maxWidth: 'min(92vw, 360px)' }}
    >
      <span className="relative shrink-0 h-10 w-10 rounded-full overflow-hidden bg-[#FAFAFA] border border-[#F0EDE5]">
        {entry.image ? (
          <img src={entry.image} alt="" className="absolute inset-0 w-full h-full object-cover" />
        ) : (
          <span className="absolute inset-0 flex items-center justify-center text-zinc-800">
            <ShoppingBag className="h-4 w-4" />
          </span>
        )}
      </span>
      <span className="min-w-0 flex flex-col leading-tight">
        <span className="text-[9.5px] font-bold uppercase tracking-[0.22em] text-zinc-800">
          Continue shopping
        </span>
        <span className="text-[12.5px] font-extrabold text-[#0e0e10] truncate">
          {entry.name}
        </span>
      </span>
      <ArrowRight className="h-3.5 w-3.5 text-[#0e0e10] shrink-0 transition-transform group-hover:translate-x-0.5" />
      <button
        type="button"
        onClick={handleDismiss}
        data-testid="home-continue-shopping-dismiss"
        aria-label="Dismiss continue shopping chip"
        className="ml-1 shrink-0 inline-flex items-center justify-center h-6 w-6 rounded-full text-[#9aa0a6] hover:text-[#0e0e10] hover:bg-[#F5F5F7] transition-colors"
      >
        <X className="h-3 w-3" />
      </button>
    </Link>
  );
}
